import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { Clock, Users, CheckCircle, AlertCircle, MessageSquare, BarChart3, FileText, Plus, Send, ThumbsUp, ThumbsDown, User, Settings, LogOut, ChevronRight, ChevronDown, ChevronUp, X, Mic, MicOff, Mail, Search, RefreshCw, Trash2, XCircle, ArrowLeft, Loader2, Bell, BellOff, Eye, Download, Filter, Columns, ArrowUpDown, Maximize2, Minimize2, ChevronLeft, ShieldAlert, Heart, Phone } from 'lucide-react';
import { useToast, useCrisisAlert, ToastProvider, CrisisContext } from '../shared.jsx';
import { storage, addAuditEntry, calculateWorkingHours, OWL_LOGO, EMAIL_CONFIG, detectHarmContent, notifyMentorOfHarmDetection, sendNotification, VoiceInputButton, useVoiceInput, initFCM, removeFCMToken, notificationPermissionGranted, requestNotificationPermission, CrisisResourceModal, getUrlParams, clearUrlParams, initFirebase } from '../shared.jsx';

function EvaluateSelectStep({ conflict, user, canEdit, onUpdate, onReturnToOverview }) {
  const toast = useToast();
  const [ratings, setRatings] = useState({});
  const [hasRated, setHasRated] = useState(false);

  // Get the early resolution if it exists
  const earlyResolution = conflict.steps?.identifyDefine?.resolutionCheck?.canResolve 
    ? conflict.steps.identifyDefine.resolutionCheck 
    : null;

  // Get all alternatives (including early resolution)
  const alternatives = conflict.steps?.exploreAlternatives?.alternatives || [];
  
  // Combine early resolution with other alternatives for rating
  const allOptions = [];
  if (earlyResolution?.resolution) {
    allOptions.push({
      id: 'early-resolution',
      text: earlyResolution.resolution,
      createdBy: earlyResolution.proposedBy,
      createdByName: earlyResolution.proposedByEmail || earlyResolution.proposedByName,
      timestamp: earlyResolution.timestamp,
      isEarlyResolution: true
    });
  }
  allOptions.push(...alternatives);

  // Check if user has already rated
  const userRatings = conflict.steps?.evaluateSelect?.ratings?.[user.id];
  
  React.useEffect(() => {
    if (userRatings) {
      setRatings(userRatings);
      setHasRated(true);
    }
  }, [userRatings]);

  const handleRatingChange = (optionId, rating) => {
    const newRating = parseInt(rating);
    // Enforce: only one option can be rated 5 (top pick)
    if (newRating === 5) {
      // Remove any existing 5-rating from other options
      const updatedRatings = { ...ratings };
      for (const key of Object.keys(updatedRatings)) {
        if (key !== optionId && updatedRatings[key] === 5) {
          delete updatedRatings[key]; // Clear the old 5 so user must re-rate it
        }
      }
      updatedRatings[optionId] = 5;
      setRatings(updatedRatings);
    } else {
      setRatings({ ...ratings, [optionId]: newRating });
    }
  };

  // Check which option currently has a 5 rating
  const fiveRatedOptionId = Object.entries(ratings).find(([, v]) => v === 5)?.[0] || null;

  const handleSubmitRatings = async () => {
    // Validate that all options are rated
    const allRated = allOptions.every(opt => ratings[opt.id] !== undefined);
    if (!allRated) {
      toast('Please rate all options before submitting');
      return;
    }

    // Validate exactly one option is rated 5
    const fiveCount = Object.values(ratings).filter(r => r === 5).length;
    if (fiveCount === 0) {
      toast('You must rate exactly one option as 5 (your top pick)');
      return;
    }
    if (fiveCount > 1) {
      toast('Only one option can be rated 5. Please adjust your ratings.');
      return;
    }

    try {
      const updated = {
        ...conflict,
        steps: {
          ...conflict.steps,
          evaluateSelect: {
            ...conflict.steps.evaluateSelect,
            ratings: {
              ...(conflict.steps.evaluateSelect?.ratings || {}),
              [user.id]: ratings
            }
          }
        },
        updatedAt: new Date().toISOString(),
        updatedBy: user.id
      };

      // Check if all mentees have rated
      const allRatings = updated.steps.evaluateSelect.ratings;
      const ratingCount = Object.keys(allRatings).length;
      const totalMentees = conflict.mentees?.length || 1;

      if (ratingCount >= totalMentees) {
        // Calculate average scores
        const scores = {};
        allOptions.forEach(opt => {
          const optionRatings = Object.values(allRatings).map(r => r[opt.id] || 0);
          const avg = optionRatings.reduce((a, b) => a + b, 0) / optionRatings.length;
          scores[opt.id] = avg;
        });

        // Find the highest rated option
        const topOption = Object.entries(scores).sort((a, b) => b[1] - a[1])[0];
        
        updated.steps.evaluateSelect.scores = scores;
        updated.steps.evaluateSelect.selected = topOption[0];
        updated.steps.evaluateSelect.completed = true;
        updated.status = 'agree-implement';
        updated.statusHistory.push({
          status: 'agree-implement',
          timestamp: new Date().toISOString()
        });
      }

      addAuditEntry(updated, user.id, user.name, 'Submitted solution ratings', 'Evaluate & Select phase', user.email);
      await storage.set(`conflict:${conflict.id}`, updated, true);
      setHasRated(true);
      await onUpdate();
      toast('Your ratings have been submitted!');
      if (onReturnToOverview) onReturnToOverview();
    } catch (error) {
      console.error('Error submitting ratings:', error);
      toast('Failed to submit ratings. Please try again.');
    }
  };

  const evaluateSelect = conflict.steps?.evaluateSelect;
  const allScores = evaluateSelect?.scores;
  const selectedId = evaluateSelect?.selected;
  const isResolved = conflict.status === 'resolved';

  return (
    <div className="space-y-6">
      <div>
        <h4 className="font-medium text-stone-800 mb-3">Evaluate & Select Solutions</h4>
        <p className="text-sm text-stone-600 mb-4">
          Rate each proposed solution on a scale of 1-5, where 1 is least preferred and 5 is most preferred.
        </p>
      </div>

      {/* Show results if all have rated */}
      {allScores && selectedId && (
        <div className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-2 border-green-200 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h5 className="font-medium text-green-800 text-lg">Selected Solution</h5>
              <p className="text-sm text-green-700">Based on everyone's ratings</p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 border border-green-300">
            <p className="text-stone-700 mb-2">
              {allOptions.find(o => o.id === selectedId)?.text}
            </p>
            <div className="flex items-center gap-4 text-sm">
              <span className="text-green-700 font-medium">
                Average Score: {allScores[selectedId]?.toFixed(1)}/5.0
              </span>
              {allOptions.find(o => o.id === selectedId)?.isEarlyResolution && (
                <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs">
                  Early Resolution
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Rating Form */}
      {!hasRated && canEdit && conflict.mentees?.some(m => m.id === user.id || m.email?.toLowerCase() === user.email?.toLowerCase()) ? (
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>Rate each solution 1-5. You may only give a <strong>5</strong> to one solution (your top pick). {fiveRatedOptionId ? 'Your top pick is highlighted below.' : 'Select your top pick by rating it 5.'}</span>
          </div>
          {allOptions.map((option) => {
            const isTopPick = ratings[option.id] === 5;
            const anotherHasFive = fiveRatedOptionId && fiveRatedOptionId !== option.id;
            return (
            <div key={option.id} className={`bg-white rounded-lg p-5 border-2 transition-colors ${isTopPick ? 'border-amber-400 bg-amber-50/30' : 'border-stone-200'}`}>
              <div className="flex items-start gap-3 mb-3">
                <div className="flex-1">
                  {option.isEarlyResolution && (
                    <div className="bg-amber-100 text-amber-700 px-2 py-1 rounded text-xs inline-block mb-2">
                      Early Resolution Proposal
                    </div>
                  )}
                  {option.mentorEditedBy && (
                    <div className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs inline-block mb-2" title={option.originalText ? `Original: "${option.originalText}"` : ''}>
                      ✏️ Mentor-Reviewed {option.originalText ? '(edited)' : ''}
                    </div>
                  )}
                  {isTopPick && (
                    <div className="bg-amber-700 text-white px-2 py-1 rounded text-xs inline-block mb-2 font-medium">
                      Your Top Pick
                    </div>
                  )}
                  <p className="text-stone-700 mb-2">{option.text}</p>
                  {hasRated && (
                    <div className="text-xs text-stone-500">
                      Proposed by {option.createdByName} · {new Date(option.timestamp).toLocaleString()}
                    </div>
                  )}
                </div>
              </div>

              <fieldset className="flex items-center gap-3 mt-4 min-w-0">
                <legend className="float-left text-sm font-medium text-stone-700">Your Rating:</legend>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((rating) => {
                    const isSelected = ratings[option.id] === rating;
                    const isFiveDisabled = rating === 5 && anotherHasFive;
                    return (
                    <label
                      key={rating}
                      className={`cursor-pointer transition-all ${
                        isSelected
                          ? 'scale-110'
                          : isFiveDisabled
                            ? 'opacity-30 cursor-not-allowed'
                            : 'opacity-60 hover:opacity-100'
                      }`}
                      title={isFiveDisabled ? 'You already rated another option as 5. Change that rating first.' : `Rate ${rating}`}
                    >
                      <input
                        type="radio"
                        name={`rating-${option.id}`}
                        value={rating}
                        checked={isSelected}
                        onChange={(e) => handleRatingChange(option.id, e.target.value)}
                        className="sr-only peer"
                        disabled={isFiveDisabled}
                      />
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center font-medium transition-all peer-focus-visible:ring-2 peer-focus-visible:ring-amber-700 peer-focus-visible:ring-offset-2 ${
                          isSelected
                            ? rating === 5 ? 'bg-amber-700 text-white shadow-lg ring-2 ring-amber-300' : 'bg-amber-700 text-white shadow-lg'
                            : isFiveDisabled
                              ? 'bg-stone-100 text-stone-300'
                              : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                        }`}
                      >
                        {rating}
                      </div>
                    </label>
                    );
                  })}
                </div>
              </fieldset>
            </div>
            );
          })}

          <button
            onClick={handleSubmitRatings}
            disabled={allOptions.some(opt => ratings[opt.id] === undefined) || !fiveRatedOptionId}
            className="w-full bg-amber-700 hover:bg-amber-800 disabled:bg-stone-300 disabled:cursor-not-allowed text-white py-4 rounded-lg font-medium transition-all transform hover:scale-[1.02] active:scale-[0.98]"
          >
            {!fiveRatedOptionId ? 'Rate one option as 5 (top pick) to submit' : 'Submit Ratings'}
          </button>
        </div>
      ) : (
        <div>
          <div className="p-6 bg-blue-50 rounded-lg border border-blue-200 mb-4">
            <p className="text-blue-800 font-medium flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Your ratings have been submitted
            </p>
            <p className="text-blue-600 text-sm mt-1">
              {allScores 
                ? 'All participants have rated. See the selected solution above.' 
                : 'Waiting for other participants to submit their ratings...'}
            </p>
          </div>

          {/* Show user's ratings */}
          <div className="space-y-3">
            <h5 className="font-medium text-stone-700">Your Ratings:</h5>
            {allOptions.map((option) => (
              <div key={option.id} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                <span className="text-sm text-stone-700 flex-1">
                  {option.text.substring(0, 60)}...
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-amber-700 font-medium">{ratings[option.id]}/5</span>
                  {allScores && (
                    <span className="text-stone-500 text-sm">
                      (avg: {allScores[option.id]?.toFixed(1)})
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {allOptions.length === 0 && (
        <div className="text-center py-12 text-stone-500">
          <BarChart3 className="w-12 h-12 mx-auto mb-3 text-stone-300" />
          <p>No solutions to evaluate yet</p>
        </div>
      )}
    </div>
  );
}

// Explore Alternatives Step

export default EvaluateSelectStep;
